// JokerBadge.jsx
import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import jocker from "../Assets/JOKER.png";
import "./JokerBadge.css";

/**
 * Props:
 *  - isjocker: boolean
 *  - size: number (px)
 *  - intervalMinutes: number
 *  - enableAutoSchedule: boolean
 */
const JokerBadge = forwardRef(({ isjocker = false, size = 100, intervalMinutes = 5, enableAutoSchedule = true }, ref) => {
  const [shaking, setShaking] = useState(false);
  const [burstParticles, setBurstParticles] = useState([]);
  const [forceShake, setForceShake] = useState(false); // scheduler-forced visibility/shake
  const particleIntervalRef = useRef(null);
  const stopTimeoutRef = useRef(null);
  const scheduleIntervalRef = useRef(null);
  const lastShakeMinuteRef = useRef(null);
  const wrapperRef = useRef(null);

  useImperativeHandle(ref, () => ({
    triggerShake: (duration = 10000) => startShake(duration),
  }), []);

  const createBurst = () => {
    const particleCount = 12;
    const particles = Array.from({ length: particleCount }).map(() => {
      const angle = Math.random() * Math.PI * 2;
      const dist = 14 + Math.random() * 48;
      const dx = Math.round(Math.cos(angle) * dist);
      const dy = Math.round(Math.sin(angle) * dist);
      const sizePx = 3 + Math.round(Math.random() * 7);
      const delay = Math.random() * 0.12;
      const duration = 0.45 + Math.random() * 0.55;
      const colors = ["#FFD700", "#FF4500", "#FF69B4", "#FFFFFF", "#FFB347"];
      const color = colors[Math.floor(Math.random() * colors.length)];
      return { dx, dy, sizePx, delay, duration, color, id: Math.random().toString(36).slice(2,9) };
    });

    setBurstParticles(prev => {
      const merged = prev.concat(particles);
      if (merged.length > 300) return merged.slice(-300);
      return merged;
    });

    setTimeout(() => {
      const ids = new Set(particles.map(p => p.id));
      setBurstParticles(prev => prev.filter(p => !ids.has(p.id)));
    }, 1200);
  };

  // Start a shake for durationMs (default 10000ms)
  const startShake = (durationMs = 10000) => {
    // clear prior timers/intervals so repeated scheduled triggers behave correctly
    if (stopTimeoutRef.current) {
      clearTimeout(stopTimeoutRef.current);
      stopTimeoutRef.current = null;
    }
    if (particleIntervalRef.current) {
      clearInterval(particleIntervalRef.current);
      particleIntervalRef.current = null;
    }

    setShaking(true);
    setForceShake(true);

    // If currently active, start fataka immediately; otherwise fataka will start if/when isjocker becomes true (handled in effect below)
    if (isjocker) {
      createBurst();
      particleIntervalRef.current = setInterval(createBurst, 700);
    }

    stopTimeoutRef.current = setTimeout(() => {
      setShaking(false);
      setForceShake(false);
      if (particleIntervalRef.current) {
        clearInterval(particleIntervalRef.current);
        particleIntervalRef.current = null;
      }
      setTimeout(() => setBurstParticles([]), 500);
      stopTimeoutRef.current = null;
    }, durationMs);
  };

  // Click: manual trigger only when isjocker true (keeps old behavior)
  const onClick = () => {
    if (isjocker) startShake(8000);
  };

  // Scheduler: triggers on exact interval boundaries (00, 05, 10, ...)
  useEffect(() => {
    if (!enableAutoSchedule || !intervalMinutes || intervalMinutes <= 0) return;

    const tick = () => {
      const now = new Date();
      const hhmm = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
      const minute = now.getMinutes();

      if (minute % intervalMinutes === 0 && lastShakeMinuteRef.current !== hhmm) {
        lastShakeMinuteRef.current = hhmm;
        // ALWAYS start the shake animation for the interval (10s) regardless of isjocker.
        startShake(8000);
      }
    };

    tick();
    scheduleIntervalRef.current = setInterval(tick, 1000);
    return () => {
      if (scheduleIntervalRef.current) clearInterval(scheduleIntervalRef.current);
      scheduleIntervalRef.current = null;
    };
  }, [enableAutoSchedule, intervalMinutes]);

  // If isjocker becomes true while a forced shake is already running (or while shaking true),
  // start the particle emitter mid-shake.
  useEffect(() => {
    if (shaking && isjocker && !particleIntervalRef.current) {
      createBurst();
      particleIntervalRef.current = setInterval(createBurst, 700);
    }

    // If isjocker becomes false while particles are running, we keep current bursts but stop new ones.
    if (!isjocker && particleIntervalRef.current) {
      clearInterval(particleIntervalRef.current);
      particleIntervalRef.current = null;
    }
  }, [isjocker, shaking]);

  // cleanup on unmount
  useEffect(() => {
    return () => {
      if (particleIntervalRef.current) clearInterval(particleIntervalRef.current);
      if (stopTimeoutRef.current) clearTimeout(stopTimeoutRef.current);
      if (scheduleIntervalRef.current) clearInterval(scheduleIntervalRef.current);
    };
  }, []);

  // Visibility rules:
  // - Show image when isjocker === true OR when a scheduled/forced shake is in progress (forceShake).
  // - Fataka (particles) only while isjocker === true (but will start mid-shake if isjocker toggles true).
  const visibleNow = !!isjocker || !!forceShake;

  return (
    <div
      ref={wrapperRef}
      className={`joker-wrapper ${shaking ? "shaking" : ""}`}
      style={{
        width: size,
        height: size,
        ["--shakeDuration"]: shaking ? "8s" : "0s",
      }}
      onClick={onClick}
      role="button"
      aria-pressed={shaking}
      title={visibleNow ? "Joker" : "No Joker"}
    >
      {visibleNow ? (
        <img
          src={jocker}
          alt="Joker"
          className="joker-image"
          style={{ width: size, height: size }}
          draggable={false}
        />
      ) : (
        <div className="joker-black" style={{ width: size, height: size }} />
      )}

      {/* Fataka only when isjocker is true */}
      {isjocker && burstParticles.length > 0 && (
        <div className="fataka-layer" aria-hidden={burstParticles.length === 0}>
          {burstParticles.map((p) => (
            <span
              key={p.id}
              className="fataka-particle"
              style={{
                width: p.sizePx,
                height: p.sizePx,
                background: p.color,
                ["--dx"]: `${p.dx}px`,
                ["--dy"]: `${p.dy}px`,
                ["--pDuration"]: `${p.duration}s`,
                ["--pDelay"]: `${p.delay}s`,
              }}
            />
          ))}
        </div>
      )}

      <div className="joker-ring" />
    </div>
  );
});

export default JokerBadge;
